import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { installEditorShortcuts } from './keyboard';
import { getAdjacentBlockId } from './selection';
import { editorStore, useEditorStore } from './editorStore';
import BlockPalette from './BlockPalette';
import StructureTree from './StructureTree';
import Canvas from './Canvas';
import Inspector from './Inspector';
import ThemePanel from './ThemePanel';
import MediaLibrary from './MediaLibrary';
import PublishDialog from './PublishDialog';
import HistoryDialog from './HistoryDialog';
import DevicePreview from './DevicePreview';
import DesignedWorkspace from './designed/DesignedWorkspace';
import GuidedTour from './tour/GuidedTour';
import { DESIGNED_TOUR_STEPS } from './tour/tourSteps';
import { Icon } from '../components/ui/Icon';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import './editor.css';

export default function EditorPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const state = useEditorStore();
  const [leftTab, setLeftTab] = useState<'palette' | 'structure'>('palette');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [leftOpen, setLeftOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return !window.matchMedia('(max-width: 1100px)').matches;
  });

  useEffect(() => {
    if (siteId) {
      const id = siteId;
      void editorStore.loadSite(id);
    }
    // editorStore.loadSite is stable and managed by the singleton store.
  }, [siteId]);

  const mobile = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(max-width: 900px)').matches;
  }, []);

  const compact = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(max-width: 1100px)').matches;
  }, []);

  useEffect(() => {
    if (!state.site) {
      return;
    }

    const cleanup = installEditorShortcuts({
      onUndo: () => editorStore.undo(),
      onRedo: () => editorStore.redo(),
      onDelete: () => {
        if (state.selectedBlockId) {
          editorStore.removeBlock(state.selectedBlockId);
        }
      },
      onSelectPrevious: () => {
        if (state.site) {
          const previous = getAdjacentBlockId(state.site.blocks, state.selectedBlockId, 'previous');
          if (previous) {
            editorStore.selectBlock(previous);
          }
        }
      },
      onSelectNext: () => {
        if (state.site) {
          const next = getAdjacentBlockId(state.site.blocks, state.selectedBlockId, 'next');
          if (next) {
            editorStore.selectBlock(next);
          }
        }
      },
      onSave: () => void editorStore.autosaveNow()
    });

    return cleanup;
  }, [
    state.site,
    state.selectedBlockId
  ]);

  if (mobile) {
    return (
      <div className="editor-shell mobile-editor-fallback">
        <p>
          Use a larger screen to edit. The preview is still available below.
        </p>
        <Canvas />
      </div>
    );
  }

  return (
    <div className="editor-shell">
      <header className="editor-toolbar">
        <div className="editor-toolbar-left">
          {compact ? (
            <button
              type="button"
              className="toolbar-icon-button"
              onClick={() => setLeftOpen((value) => !value)}
              aria-label="Toggle blocks panel"
              aria-expanded={leftOpen}
            >
              <Icon name="panel-left" size={18} />
            </button>
          ) : null}
          <Link to="/dashboard/sites" className="toolbar-icon-button" aria-label="Back to my sites">
            <Icon name="chevron-left" size={18} />
          </Link>
          <span className="editor-toolbar-title">{state.site?.meta.title ?? 'Editor'}</span>
          <span
            className={`editor-save-state editor-save-state--${state.saveState}`}
            role="status"
            aria-live="polite"
          >
            <span className="editor-save-state-dot" aria-hidden="true" />
            {state.saveState}
            {state.lastSavedAt ? ` · ${new Date(state.lastSavedAt).toLocaleTimeString()}` : ''}
          </span>
        </div>
        <div className="editor-toolbar-center" data-tour="device">
          <DevicePreview />
        </div>
        <div className="editor-toolbar-actions">
          <button
            type="button"
            className="toolbar-icon-button"
            onClick={() => editorStore.undo()}
            aria-label="Undo"
            data-tour="undo"
          >
            <Icon name="undo" size={17} />
            <span className="toolbar-button-label">Undo</span>
          </button>
          <button
            type="button"
            className="toolbar-icon-button"
            onClick={() => editorStore.redo()}
            aria-label="Redo"
          >
            <Icon name="redo" size={17} />
            <span className="toolbar-button-label">Redo</span>
          </button>
          <button
            type="button"
            className="toolbar-icon-button"
            onClick={() => void editorStore.autosaveNow()}
            aria-label="Save"
          >
            <Icon name="save" size={17} />
            <span className="toolbar-button-label">Save</span>
          </button>
          <button
            type="button"
            className="toolbar-icon-button"
            onClick={() => setHistoryOpen(true)}
            aria-label="Version history"
            data-tour="history"
          >
            <Icon name="clock" size={17} />
            <span className="toolbar-button-label">History</span>
          </button>
          <button
            type="button"
            className="toolbar-icon-button"
            onClick={() => window.dispatchEvent(new CustomEvent('sarnia:start-tour'))}
            aria-label="Guide"
          >
            <Icon name="help" size={17} />
            <span className="toolbar-button-label">Guide</span>
          </button>
          <ThemeToggle />
          <button
            type="button"
            className="toolbar-button toolbar-button--primary"
            onClick={() => editorStore.selectPanel('publish')}
            data-tour="publish-button"
            aria-label="Share"
          >
            <Icon name="rocket" size={16} />
            <span className="toolbar-button-label">Share</span>
          </button>
        </div>
      </header>

      <HistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />

      <GuidedTour steps={state.site?.blocks[0]?.type === 'designed' ? DESIGNED_TOUR_STEPS : undefined} />

      {state.site?.blocks[0]?.type === 'designed' ? (
        <div className="editor-workspace editor-workspace--designed">
          <DesignedWorkspace />
        </div>
      ) : (
        <div className="editor-workspace">
          <aside className={`editor-left ${leftOpen ? 'editor-left--open' : ''}`} data-tour="palette">
            <div className="editor-tabs" role="tablist" aria-label="Blocks panel">
              <button
                type="button"
                className={`editor-tab ${leftTab === 'palette' ? 'editor-tab--active' : ''}`}
                onClick={() => setLeftTab('palette')}
                aria-pressed={leftTab === 'palette'}
                title="Blocks"
                aria-label="Blocks"
              >
                <Icon name="layout" size={16} />
                <span className="editor-tab-text">Blocks</span>
              </button>
              <button
                type="button"
                className={`editor-tab ${leftTab === 'structure' ? 'editor-tab--active' : ''}`}
                onClick={() => setLeftTab('structure')}
                aria-pressed={leftTab === 'structure'}
                title="Structure"
                aria-label="Structure"
                data-tour="tab-structure"
              >
                <Icon name="layers" size={16} />
                <span className="editor-tab-text">Structure</span>
              </button>
            </div>
            {leftTab === 'palette' ? <BlockPalette /> : <StructureTree />}
          </aside>
          <main className="editor-canvas" data-tour="canvas">
            <Canvas />
          </main>
          <aside className="editor-right" data-tour="inspector">
            <div className="editor-tabs" role="tablist" aria-label="Inspector panels">
              <button
                type="button"
                className={`editor-tab ${state.selectedPanel === 'block' ? 'editor-tab--active' : ''}`}
                onClick={() => editorStore.selectPanel('block')}
                aria-pressed={state.selectedPanel === 'block'}
                title="Inspector"
                aria-label="Inspector"
              >
                <Icon name="edit" size={16} />
                <span className="editor-tab-text">Inspector</span>
              </button>
              <button
                type="button"
                className={`editor-tab ${state.selectedPanel === 'theme' ? 'editor-tab--active' : ''}`}
                onClick={() => editorStore.selectPanel('theme')}
                aria-pressed={state.selectedPanel === 'theme'}
                title="Theme"
                aria-label="Theme"
                data-tour="tab-theme"
              >
                <Icon name="palette" size={16} />
                <span className="editor-tab-text">Theme</span>
              </button>
              <button
                type="button"
                className={`editor-tab ${state.selectedPanel === 'media' ? 'editor-tab--active' : ''}`}
                onClick={() => editorStore.selectPanel('media')}
                aria-pressed={state.selectedPanel === 'media'}
                title="Media"
                aria-label="Media"
                data-tour="tab-media"
              >
                <Icon name="image" size={16} />
                <span className="editor-tab-text">Media</span>
              </button>
              <button
                type="button"
                className={`editor-tab ${state.selectedPanel === 'publish' ? 'editor-tab--active' : ''}`}
                onClick={() => editorStore.selectPanel('publish')}
                aria-pressed={state.selectedPanel === 'publish'}
                title="Share"
                aria-label="Share"
                data-tour="tab-publish"
              >
                <Icon name="rocket" size={16} />
                <span className="editor-tab-text">Share</span>
              </button>
            </div>
            {state.selectedPanel === 'block' ? <Inspector /> : null}
            {state.selectedPanel === 'theme' ? <ThemePanel /> : null}
            {state.selectedPanel === 'media' ? <MediaLibrary /> : null}
            {state.selectedPanel === 'publish' ? <PublishDialog /> : null}
          </aside>
        </div>
      )}
    </div>
  );
}
